import { useState } from 'react';
import { Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useOrganization } from '@/lib/organization-context';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface HelpSupportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select a category when opening (e.g. "Bug report" for the report-a-problem flow). */
  initialCategory?: Category;
  /** Title shown at the top of the dialog. */
  title?: string;
}

type Category = 'Question' | 'Bug report' | 'Feature request';

export function HelpSupportDialog({
  open,
  onOpenChange,
  initialCategory,
  title = 'Help & Support',
}: HelpSupportDialogProps) {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const [category, setCategory] = useState<Category | ''>(initialCategory ?? '');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Re-sync category when re-opened with a different initialCategory
  if (open && initialCategory && category !== initialCategory && !message && status === 'idle') {
    // safe: only runs on render when state should mirror prop
    setTimeout(() => setCategory(initialCategory), 0);
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !category || status === 'sending') return;

    setStatus('sending');
    setErrorMessage('');

    try {
      const currentPage = typeof window !== 'undefined' ? window.location.href : '';
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      const viewport =
        typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '';
      const proposalIdMatch = currentPage.match(/\/projects\/([^/?#]+)/);
      const proposalId = proposalIdMatch ? proposalIdMatch[1] : '';

      const contextLines = [
        `Page: ${currentPage}`,
        proposalId ? `Proposal ID: ${proposalId}` : '',
        organization?.id ? `Org ID: ${organization.id}` : '',
        organization?.name ? `Org: ${organization.name}` : '',
        viewport ? `Viewport: ${viewport}` : '',
        userAgent ? `Browser: ${userAgent}` : '',
      ].filter(Boolean);

      const messageWithContext = `${message.trim()}\n\n---\n${contextLines.join('\n')}`;

      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: user?.user_metadata?.name || user?.email?.split('@')[0] || 'User',
          email: user?.email || '',
          company: organization?.name || '',
          requestType: category,
          message: messageWithContext,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setStatus('sent');
      setMessage('');
      setCategory('');
    } catch (err: any) {
      console.error('Failed to send support message:', err);
      setStatus('error');
      setErrorMessage(err?.message || 'Something went wrong. Please try again.');
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset state when closing, but keep 'sent' briefly visible
      setTimeout(() => {
        setStatus('idle');
        setErrorMessage('');
      }, 300);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {status === 'sent' ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <CheckCircle2 className="w-10 h-10 text-primary" />
            <p className="text-sm font-medium text-foreground">Message sent!</p>
            <p className="text-sm text-muted-foreground">
              We'll get back to you at <span className="font-medium">{user?.email}</span> as soon as possible.
            </p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <p className="text-sm font-medium text-foreground">
              Need help or have a question?
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Send us a message here or email us at{' '}
              <a href="mailto:support@sorvaine.com" className="text-primary hover:underline">
                support@sorvaine.com
              </a>
              . We reply within 1 business day.
            </p>

            <div className="space-y-2">
              <Label htmlFor="support-category" className="text-sm">
                Category
              </Label>
              <Select value={category} onValueChange={(v) => setCategory(v as Category)}>
                <SelectTrigger id="support-category">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Question">Question</SelectItem>
                  <SelectItem value="Bug report">Bug report</SelectItem>
                  <SelectItem value="Feature request">Feature request</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="support-message" className="text-sm">
                Your message
              </Label>
              <Textarea
                id="support-message"
                placeholder="Describe your issue or question..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="min-h-[120px] resize-none"
                maxLength={2000}
                required
              />
              <p className="text-[11px] text-muted-foreground text-right">
                {message.length}/2000
              </p>
            </div>

            {status === 'error' && (
              <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-md p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <p className="text-[11px] text-muted-foreground">
                Sending as {user?.email}
              </p>
              <Button
                type="submit"
                size="sm"
                disabled={!message.trim() || !category || status === 'sending'}
              >
                {status === 'sending' ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5 mr-1.5" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
